import { useNavigate } from "react-router-dom";
import { Star, Wifi, Plug, MapPin, BadgeCheck, Coffee, Clock, Phone, Users, Sun } from "lucide-react";
import { PriceDisplay } from "./PriceRating";
import { getVenueImage } from "../utils/defaultImages";
import WiFiSpeedGauge, { WiFiSpeedBadge, getSpeedTier, SPEED_TIERS } from "./WiFiSpeedGauge";

const wifiLabels = {
  very_fast: "Very Fast",
  fast: "Fast WiFi",
  good: "Good WiFi",
  poor: "Poor WiFi",
  very_poor: "Very Poor",
  none: "No WiFi",
  excellent: "Very Fast",
  fair: "Poor WiFi"
};

// Format opening hours for display
const formatHours = (hours) => {
  if (!hours) return null;
  // If it's a simple string like "9am - 6pm" or "09:00 - 18:00"
  if (hours.length < 30) return hours;
  // Otherwise truncate
  return hours.substring(0, 25) + "...";
};

export default function VenueCard({ venue, compact = false }) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/venue/${venue.id}`);
  };

  const imageUrl = getVenueImage(venue, 400);
  const fallbackUrl = getVenueImage({ venue_type: venue.venue_type }, 400);
  const neighborhood = venue.neighborhood || venue.location?.city;

  if (compact) {
    return (
      <div
        onClick={handleClick}
        className="venue-card cursor-pointer"
        data-testid={`venue-card-${venue.id}`}
        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', width: '100%' }}
      >
        {/* FIXED SIZE IMAGE CONTAINER */}
        <div style={{ 
          width: '56px', 
          height: '56px', 
          minWidth: '56px',
          maxWidth: '56px',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#f3f4f6',
          position: 'relative',
          flexShrink: 0
        }}>
          <img
            src={imageUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
            onError={(e) => {
              e.target.src = fallbackUrl;
            }}
          />
          {venue.is_premium && (
            <div style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              backgroundColor: '#f59e0b',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Star style={{ width: '10px', height: '10px', color: 'white', fill: 'white' }} />
            </div>
          )}
        </div>

        {/* TEXT CONTENT - Flex grow with overflow control */}
        <div style={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden' }}>
          <span style={{ 
            fontSize: '10px', 
            fontWeight: 500, 
            color: '#2563eb', 
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {venue.venue_type?.replace("_", " ")}
          </span>
          <h3 style={{ 
            fontWeight: 600, 
            color: '#111827', 
            fontSize: '14px',
            marginTop: '2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {venue.name}
          </h3>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px', 
            marginTop: '3px',
            fontSize: '11px',
            color: '#6b7280',
            flexWrap: 'nowrap'
          }}>
            <MapPin style={{ width: '11px', height: '11px', flexShrink: 0 }} />
            <span style={{ 
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '70px'
            }}>{neighborhood}</span>
            <span style={{ flexShrink: 0 }}>•</span>
            <Star style={{ width: '11px', height: '11px', color: '#f59e0b', fill: '#f59e0b', flexShrink: 0 }} />
            <span style={{ flexShrink: 0 }}>{venue.rating?.toFixed(1)}</span>
            {venue.distance_km !== undefined && (
              <>
                <span style={{ flexShrink: 0 }}>•</span>
                <span style={{ fontWeight: 500, color: '#2563eb', flexShrink: 0 }}>{venue.distance_km}km</span>
              </>
            )}
          </div>
        </div>

        {/* Right chevron indicator */}
        <div style={{ 
          flexShrink: 0, 
          display: 'flex', 
          alignItems: 'center',
          color: '#9ca3af',
          paddingLeft: '4px'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
      </div>
    );
  }

  // Full card layout
  return (
    <div
      onClick={handleClick}
      className="venue-card cursor-pointer group"
      data-testid={`venue-card-${venue.id}`}
      style={{ position: 'relative' }}
    >
      {/* Image container */}
      <div className="relative overflow-hidden" style={{ height: '160px', zIndex: 1 }}>
        <img
          src={imageUrl}
          alt={venue.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          style={{ display: 'block' }}
          onError={(e) => {
            e.target.src = fallbackUrl;
          }}
        />
        
        {/* Top badges row */}
        <div className="absolute top-2 left-2 right-2 flex items-start justify-between" style={{ zIndex: 2 }}>
          <div className="flex gap-1.5 flex-wrap">
            {venue.is_premium && (
              <div className="badge-premium">Featured</div>
            )}
            {venue.verified_workspace && (
              <div className="px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded-full flex items-center gap-1 shadow">
                <BadgeCheck className="w-3 h-3" />
                Verified Workspace
              </div>
            )}
            {venue.is_claimed && venue.is_verified && !venue.verified_workspace && (
              <div className="badge-verified">
                <BadgeCheck className="w-3 h-3" />
                Verified
              </div>
            )}
          </div>
          
          {/* WiFi Speed Gauge - Top right corner */}
          {venue.wifi_speed_mbps > 0 && (
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-1 shadow-sm">
              <WiFiSpeedGauge 
                speedMbps={venue.wifi_speed_mbps} 
                size="sm" 
                showLabel={false}
              />
            </div>
          )}
        </div>

        {/* Opening Hours Badge - Bottom of image */}
        {venue.opening_hours && (
          <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center" style={{ zIndex: 2 }}>
            <div className="flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg">
              <Clock className="w-3 h-3" />
              <span>{formatHours(venue.opening_hours)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Text content */}
      <div className="p-4 bg-white" style={{ position: 'relative', zIndex: 10, minHeight: '140px' }}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-brand uppercase tracking-wide">
                {venue.venue_type?.replace("_", " ")}
              </span>
              {venue.neighborhood && venue.neighborhood !== venue.location?.city && (
                <>
                  <span className="text-xs text-gray-300">•</span>
                  <span className="text-xs text-gray-500">{venue.neighborhood}</span>
                </>
              )}
            </div>
            <h3 className="font-semibold text-gray-900 text-base truncate" title={venue.name}>
              {venue.name}
            </h3>
          </div>
        </div>

        {/* Location and Rating Row */}
        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          <span className="truncate max-w-[100px]">{venue.location?.city}</span>
          <span>•</span>
          <Star className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0" />
          <span className="font-medium">{venue.rating?.toFixed(1)}</span>
          <span className="text-gray-400">({venue.review_count || 0})</span>
          {venue.distance_km !== undefined && (
            <>
              <span>•</span>
              <span className="font-medium text-brand">{venue.distance_km}km</span>
            </>
          )}
        </div>

        {/* Description - 2 lines */}
        <p className="mt-2 text-sm text-gray-600 line-clamp-2 overflow-hidden">
          {venue.description}
        </p>

        {/* Amenity Badges Row - Limited on mobile */}
        <div className="mt-3 flex flex-wrap gap-1.5 overflow-hidden">
          {/* WiFi Speed (if measured) */}
          {venue.wifi_speed_mbps ? (() => {
            const tier = getSpeedTier(venue.wifi_speed_mbps);
            const tierData = tier ? SPEED_TIERS[tier] : null;
            return (
              <span 
                className="badge-amenity flex items-center gap-1 flex-shrink-0 font-medium"
                style={{ 
                  backgroundColor: tierData?.bgColor || '#F3F4F6',
                  color: tierData?.color || '#374151',
                  borderColor: tierData?.color || '#D1D5DB'
                }}
              >
                <Wifi className="w-3 h-3" />
                <span className="font-semibold">{Math.round(venue.wifi_speed_mbps)}</span>
                <span className="text-xs opacity-75">Mbps</span>
                <span className="hidden sm:inline text-xs">• {tierData?.label || 'WiFi'}</span>
              </span>
            );
          })() : (
            <span className="badge-amenity flex items-center gap-1 flex-shrink-0">
              <Wifi className="w-3 h-3" />
              <span className="hidden sm:inline">{wifiLabels[venue.amenities?.wifi_quality] || venue.amenities?.wifi_quality || "N/A"}</span>
              <span className="sm:hidden">WiFi</span>
            </span>
          )}
          
          {/* Plug Sockets Count (if specified) */}
          {venue.plug_sockets_count ? (
            <span className="badge-amenity flex items-center gap-1 flex-shrink-0 bg-blue-50 text-blue-700 border-blue-200">
              <Plug className="w-3 h-3" />
              <span className="font-semibold">{venue.plug_sockets_count}</span> plugs
            </span>
          ) : (
            <span className="badge-amenity flex items-center gap-1 flex-shrink-0">
              <Plug className="w-3 h-3" />
              {venue.amenities?.power_outlets || 0}+
            </span>
          )}
          
          {/* Coffee Price - Compact on mobile */}
          {venue.price_range?.coffee_avg > 0 && (
            <span className="badge-amenity flex items-center gap-1 flex-shrink-0">
              <Coffee className="w-3 h-3" />
              <span className="hidden sm:flex">
                <PriceDisplay value={venue.price_range.coffee_avg} size="sm" />
              </span>
              <span className="sm:hidden text-green-600 font-medium">
                {"£".repeat(Math.min(Math.ceil(venue.price_range.coffee_avg), 3))}
              </span>
            </span>
          )}

          {/* Conditional Amenity Badges - Hide some on mobile to prevent overflow */}
          {venue.amenities?.good_for_calls && (
            <span className="badge-amenity flex items-center gap-1 bg-green-50 text-green-700 border-green-200 flex-shrink-0">
              <Phone className="w-3 h-3" />
              <span className="hidden sm:inline">Calls OK</span>
              <span className="sm:hidden">Calls</span>
            </span>
          )}
          
          {venue.amenities?.long_stay_friendly && (
            <span className="badge-amenity items-center gap-1 bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0 hidden sm:flex">
              <Clock className="w-3 h-3" />
              Long Stay
            </span>
          )}
          
          {venue.amenities?.group_friendly && (
            <span className="badge-amenity items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 flex-shrink-0 hidden sm:flex">
              <Users className="w-3 h-3" />
              Groups
            </span>
          )}
          
          {venue.amenities?.natural_light && (
            <span className="badge-amenity items-center gap-1 bg-yellow-50 text-yellow-700 border-yellow-200 flex-shrink-0 hidden sm:flex">
              <Sun className="w-3 h-3" />
              Light
            </span>
          )}
          
          {venue.amenities?.is_spacious && (
            <span className="badge-amenity flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 flex-shrink-0">
              <Users className="w-3 h-3" />
              <span className="hidden sm:inline">Spacious</span>
              <span className="sm:hidden">Big</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
